import { makeAutoObservable, action } from 'mobx';
import {
  fetchFavorites,
  removeFromFavoritesById,
  addToFavoritesById
} from '../api/FavoritingService';
import { updateHiredStatus } from '../api/SlmService';
import notificationStore from './NotificationStore';

class FavoritesStore {
  favorites = [];

  userInitiatedFavorites = new Set([]);

  userInitiatedUnFavorites = new Set([]);

  isFavoritesLoading = false;

  favoritesPaginationInfo = {};

  page = 1;

  sort = {
    by: null,
    order: null
  };

  safeItemRemove = false;

  _navigate = null;

  _query = null;

  /* Used in case when remove last item on page and page value should be decremented by 1 before data fetch */
  setSafeItemRemove = (value, query = null, navigate = null) => {
    this.safeItemRemove = value;
    this._query = query;
    this._navigate = navigate;
  };

  notificationStore = {};

  constructor(newNotificationStore) {
    this.notificationStore = newNotificationStore;
    makeAutoObservable(this);
  }

  resetSort = () => {
    this.sort = {
      by: null,
      order: null
    };
  };

  setSort = (next) => {
    this.sort = { ...next };
  };

  setPage = (p) => {
    this.page = p;
  };

  setFavorites = (list) => {
    this.favorites = list;
  };

  setFavoritesPaginationInfo = (paginationInfo) => {
    this.favoritesPaginationInfo = paginationInfo;
  };

  setIsFavoritesLoading = (bool) => {
    this.isFavoritesLoading = bool;
  };

  fetchFavorites = (withLoader = true) => {
    if (withLoader) {
      this.setIsFavoritesLoading(true);
    }
    fetchFavorites(this.page, this.sort)
      .then(
        action('success', (res) => {
          this.setFavorites(res.favoriteCandidates);
          this.setFavoritesPaginationInfo(res.metadata.paginationInfo);
        }),
        action('error', () => {
          this.setFavorites([]);
          this.notificationStore.setError('Failed to retrieve favorites list.');
        })
      )
      .finally(() => {
        this.setIsFavoritesLoading(false);
      });
  };

  _removeFromFavorites = (candidateId) => {
    const oldList = [...this.favorites];
    const tempList = [...this.favorites];
    const idx = tempList.findIndex((item) => item.candidateId === candidateId);
    tempList.splice(idx, 1);
    this.setFavorites(tempList);
    this._userInitiatedRemoveFromFavorites(candidateId);

    const isRemovableCriteria = [
      this.safeItemRemove,
      this._query,
      this._navigate,
      !tempList.length,
      this.page > 1
    ];

    if (isRemovableCriteria.every((removableCriteria) => !!removableCriteria === true)) {
      this._query.set('page', this.page - 1);
      this._navigate({ search: this._query.toString() });
    }

    removeFromFavoritesById(candidateId).then(
      action('success', () => {
        this.fetchFavorites(false);
      }),
      action('error', () => {
        this._userInitiatedRemoveFromFavorites(candidateId);
        this.setFavorites(oldList);
        this.notificationStore.setError('Failed to remove candidate from favorites list.');
      })
    );
  };

  _userInitiatedAddToFavorites(candidateId) {
    this.userInitiatedFavorites.add(candidateId);
    this.userInitiatedUnFavorites.delete(candidateId);
  }

  _userInitiatedRemoveFromFavorites(candidateId) {
    this.userInitiatedFavorites.delete(candidateId);
    this.userInitiatedUnFavorites.add(candidateId);
  }

  _addToFavorites = async (candidateId) => {
    this._userInitiatedAddToFavorites(candidateId);
    addToFavoritesById(candidateId).then(
      action('success', () => {
        this.fetchFavorites(false);
      }),
      action('error', (error) => {
        this._userInitiatedRemoveFromFavorites(candidateId);
        console.log('ERROR UPDATE:', error);
      })
    );
  };

  isFavorited = (candidate) => {
    return (
      this.userInitiatedFavorites.has(candidate.candidateId)
      || (candidate.favorite && !this.userInitiatedUnFavorites.has(candidate.candidateId))
    );
  };

  toggleCandidateFavoriteStatus = (candidate) => {
    if (this.isFavorited(candidate)) {
      // clickEvents.profileImageFavoriteInteraction(false);
      this._removeFromFavorites(candidate.candidateId);
    } else {
      // clickEvents.profileImageFavoriteInteraction(true);
      this._addToFavorites(candidate.candidateId);
    }
  };

  updateFavoriteHiredStatus = (candidate) => {
    const tempList = [...this.favorites];
    const oldList = [...this.favorites];
    const idx = tempList.findIndex((item) => item.candidateId === candidate.candidateId);
    const updatedCandidate = {
      ...candidate,
      candidateHired: !candidate.candidateHired
    };
    tempList.splice(idx, 1, updatedCandidate);
    this.setFavorites(tempList);

    updateHiredStatus(candidate.candidateId, !candidate.candidateHired).then(
      action('success', () => {
        this.fetchFavorites(false);
      }),
      action('error', () => {
        this.setFavorites(oldList);
        this.notificationStore.setError('Failed to toggle candidate hired status.');
      })
    );
  };
}

export default new FavoritesStore(notificationStore);
